import Titulo from '../../components/tipografia/Titulo';
import LayoutUsuario from '../../components/navegacao/LayoutUsuario';
import VBox from '../../components/layout/VBox';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import Subtitulo from '@/components/tipografia/Subtitulo';
import { format } from 'date-fns';
import HBox from '@/components/layout/HBox';
import Icon from '@mdi/react';
import { mdiFileDocument, mdiFileExcel, mdiFilePdfBox, mdiFileWord, mdiTable } from '@mdi/js';
import Descricao from '@/components/tipografia/Descricao';

type Response = {
  status: number;
  ok?: boolean;
  result: any;
};

type Props = {};

// Mapeia tipo para ícone e cor
const tipoConfig: Record<string, { icon: string; color: string; label: string }> = {
  pdf: { icon: mdiFilePdfBox, color: 'var(--tc-color-danger)', label: 'PDF' },
  docx: { icon: mdiFileWord, color: 'var(--tc-color-info)', label: 'Word' },
  csv: { icon: mdiFileExcel, color: 'var(--tc-color-success)', label: 'CSV' },
  xlsx: { icon: mdiFileExcel, color: 'var(--tc-color-success)', label: 'Excel' },
  texto: { icon: mdiFileDocument, color: 'var(--tc-color-gray-600)', label: 'Texto' },
  markdown: { icon: mdiFileDocument, color: 'var(--tc-color-gray-600)', label: 'Markdown' },
};

const Page = ({ }: Props) => {
  const { id } = useParams();

  const queryList = useQuery(['base_conhecimento', id], async () => {
    const res = await axios.get<Response>(`/api/base_conhecimento/${id}`, {
      withCredentials: true,
    });

    return res.data;
  });

  const item = queryList.data?.result;
  const dt = item?.data_ultima_atualizacao
    ? format(item.data_ultima_atualizacao, 'dd/MM/yyyy HH:mm')
    : '';

  const tipo = item?.tipo || 'texto';
  const config = tipoConfig[tipo] || tipoConfig.texto;
  const isSpreadsheet = tipo === 'csv' || tipo === 'xlsx';

  return (
    <LayoutUsuario>
      {item && (
        <VBox gap="16px" style={{ height: 'calc(100vh - 32px)' }}>
          <HBox stretch style={{ alignItems: 'flex-start' }}>
            <VBox gap="4px">
              <HBox gap="8px" style={{ alignItems: 'center' }}>
                <Icon path={config.icon} size="24px" color={config.color} />
                <Titulo>{item.titulo}</Titulo>
              </HBox>
              {item.arquivo_original && (
                <Descricao style={{ fontSize: '0.85rem', color: 'var(--tc-color-gray-500)' }}>
                  Arquivo: {item.arquivo_original}
                </Descricao>
              )}
            </VBox>
            <VBox style={{ alignItems: 'flex-end' }}>
              <div style={{ color: 'var(--tc-color-gray-500)', fontSize: '0.85rem' }}>
                Última atualização: {dt}
              </div>
              {item.metadados?.totalLinhas && (
                <div style={{ color: 'var(--tc-color-gray-500)', fontSize: '0.85rem' }}>
                  Total de linhas: {item.metadados.totalLinhas.toLocaleString('pt-BR')}
                </div>
              )}
            </VBox>
          </HBox>

          {/* Schema das colunas (para CSV/XLSX) */}
          {isSpreadsheet && item.metadados?.schema?.colunas && (
            <div style={{
              padding: '12px',
              backgroundColor: 'var(--tc-color-gray-100)',
              borderRadius: '8px',
              border: '1px solid var(--tc-color-gray-200)'
            }}>
              <HBox gap="8px" style={{ alignItems: 'center', marginBottom: '8px' }}>
                <Icon path={mdiTable} size="16px" color="var(--tc-color-gray-600)" />
                <strong style={{ fontSize: '0.9rem' }}>Colunas do documento</strong>
              </HBox>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {item.metadados.schema.colunas.map((col: string) => (
                  <span
                    key={col}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: 'var(--tc-color-gray-200)',
                      borderRadius: '4px',
                      fontSize: '0.8rem'
                    }}
                  >
                    {col}
                    {item.metadados.schema.tipos?.[col] && (
                      <span style={{ color: 'var(--tc-color-gray-500)', marginLeft: '4px' }}>
                        ({item.metadados.schema.tipos[col]})
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {item.secoes?.map((secao: any) => (
            <div
              key={secao.id}
              id={`secao-${secao.id}`}
              style={{ marginBottom: '16px' }}
            >
              <Subtitulo>{secao.titulo}</Subtitulo>

              {/* Para seções com metadados de linhas (CSV), mostra tabela */}
              {secao.metadados?.linhas && secao.metadados.linhas.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <Descricao style={{ marginBottom: '8px', fontSize: '0.8rem' }}>
                    Linhas {secao.metadados.linhaInicial} a {secao.metadados.linhaFinal}
                    ({secao.metadados.linhas.length} registros)
                  </Descricao>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.85rem',
                    border: '1px solid var(--tc-color-gray-300)'
                  }}>
                    <thead>
                      <tr style={{ backgroundColor: 'var(--tc-color-gray-100)' }}>
                        {Object.keys(secao.metadados.linhas[0]).map((col) => (
                          <th key={col} style={{
                            padding: '8px',
                            textAlign: 'left',
                            borderBottom: '2px solid var(--tc-color-gray-300)'
                          }}>
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {secao.metadados.linhas.slice(0, 10).map((linha: any, idx: number) => (
                        <tr key={idx} style={{ borderBottom: '1px solid var(--tc-color-gray-200)' }}>
                          {Object.values(linha).map((val: any, colIdx: number) => (
                            <td key={colIdx} style={{ padding: '6px 8px' }}>
                              {val?.toString() || ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {secao.metadados.linhas.length > 10 && (
                    <Descricao style={{ marginTop: '8px', fontSize: '0.8rem', color: 'var(--tc-color-gray-500)' }}>
                      ... e mais {secao.metadados.linhas.length - 10} linhas
                    </Descricao>
                  )}
                </div>
              ) : (
                <p style={{ whiteSpace: 'pre-wrap' }}>{secao.conteudo}</p>
              )}
            </div>
          ))}
        </VBox>
      )}
    </LayoutUsuario>
  );
};

export default Page;
